import { removeQueryParam } from '@prophecy/utils/history';
import { appendQueryParams } from '@prophecy/utils/url';

import {
  getAllIdpTypes,
  oAuthAzureADAuthenticate,
  oAuthGoogleAuthenticate,
  oAuthSamlAuthenticate
} from '../../../data/apis/api';
import { AuthTypes } from '../../../data/apis/types';
import { clearSsoLoginHint, hasSsoLoginHint, isSsoAutoLoginEnabled, markSsoAttempt } from './state';

import type { OAuthGoogleAuthenticatePayload } from '../../../data/apis/types';

const AUTO_LOGIN_PARAM = 'autoLogin';

const GATE_TIMEOUT_MS = 8000;

export type AutoLoginConnector = { id: string; type: AuthTypes };

type OAuthResponse = { action: string; SAMLRequest?: string; RelayState?: string };

const EMPTY_OAUTH_PAYLOAD: OAuthGoogleAuthenticatePayload = { productType: undefined };

const withTimeout = <T>(work: Promise<T>) =>
  Promise.race([work, new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), GATE_TIMEOUT_MS))]);

// Only the exact value `false`; anything else would be a crafted-link redirect to the IDP.
export function applyAutoLoginBypass(search: string) {
  if (new URLSearchParams(search).get(AUTO_LOGIN_PARAM) !== 'false') return false;
  clearSsoLoginHint();
  removeQueryParam(AUTO_LOGIN_PARAM);
  return true;
}

async function findConnector() {
  if (!isSsoAutoLoginEnabled()) return undefined;
  if (!hasSsoLoginHint()) return undefined;

  const resp = await withTimeout(getAllIdpTypes() as Promise<{ data?: { autoLoginConnector?: AutoLoginConnector } }>);
  return resp?.data?.autoLoginConnector;
}

async function resolveAuthUrl(connector: AutoLoginConnector) {
  switch (connector.type) {
    case AuthTypes.saml: {
      const resp = await withTimeout(oAuthSamlAuthenticate({}) as Promise<OAuthResponse>);
      // appendQueryParams on an empty action returns a truthy relative string.
      if (!resp?.action || !resp.SAMLRequest) return undefined;
      return appendQueryParams(resp.action, { SAMLRequest: resp.SAMLRequest, RelayState: resp.RelayState ?? '' });
    }
    case AuthTypes.azuread:
    case AuthTypes.google: {
      const start = connector.type === AuthTypes.azuread ? oAuthAzureADAuthenticate : oAuthGoogleAuthenticate;
      const resp = await withTimeout(start(EMPTY_OAUTH_PAYLOAD) as Promise<OAuthResponse>);
      return resp?.action || undefined;
    }
    default:
      return undefined;
  }
}

// Only this module reaches api.ts; ./state is the half api.ts imports, so it must stay import-free.
export async function runSsoAutoLoginGate() {
  try {
    const connector = await findConnector();
    if (!connector) return false;

    const url = await resolveAuthUrl(connector);
    if (!url) return false;

    markSsoAttempt();
    window.location.replace(url);
    return true;
  } catch {
    return false;
  }
}
